import type {
  ThemeOptions,
  ComponentsProps,
  ComponentsOverrides,
  ComponentsVariants
} from '@mui/material/styles';
import type { Theme } from '@ui/ThemeRegistry/theme.types';

const defaultProps: ComponentsProps['HeaderNavGroup'] = {};

const styleOverrides: ComponentsOverrides<Theme>['HeaderNavGroup'] = {
  root: ({ theme }) => ({
    'position': 'relative',
    '&>[class*=navItemSubMenu]': {
      position: 'absolute',
      top: '100%',
      // left: '50%', // Center the submenu
      // transform: 'translateX(-50%)', // Center the submenu
      zIndex: 1000
    },
    // Make submenu visible by default when hovering over Solutions
    '&:hover, &:focus-within': {
      '&>[class*=navItemSubMenu]': {
        maxHeight: '80vh', // Large value to ensure it's fully visible
        padding: 'var(--grid-gap-half)',
        overflow: 'visible',
        opacity: 1,
        visibility: 'visible'
      }
    },
    '&:not(:hover, :focus-within)': {
      '&>[class*=navItemSubMenu]': {
        maxHeight: 0,
        padding: 0,
        overflow: 'hidden',
        opacity: 0,
        visibility: 'hidden'
      }
    },
    // Prevent hover effects on column headers
    '& [data-variant="group"]': {
      '&:hover, &:focus': {
        '& > [class*=NavItemSubMenu]': {
          display: 'none !important',
          visibility: 'hidden !important',
          opacity: '0 !important',
          pointerEvents: 'none !important'
        }
      }
    }
  }),

  navItemLink: ({ theme }) => ({
    'padding': 0,
    'border': 'none',
    'flexGrow': '1',
    'alignItems': 'center',
    'display': 'flex',
    'width': '100%',
    'cursor': 'pointer',
    // 'fontWeight': 800,
    '&:is(:hover, :focus-within):not(:focus-visible)': {
      // TODO: ADA recommends all links are underlined, so we should not do the below, but we can add custom styles here
      // textDecoration: 'none',
      // fontWeight: 800
    },

    // TODO: Standardizxe this across the header links if they're the same
    '.MuiSvgIcon-root': {
      fill: theme.vars.palette.primary.main,
      width: 'auto',
      height: '16px',
      paddingLeft: 'var(--grid-gap-quarter)'
    }
  }),

  navItemSubMenu: ({ theme }) => ({
    // backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    borderRadius: theme.shape.borderRadius,
    transition: 'max-height 0.3s ease, opacity 0.3s ease, visibility 0.3s',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 'var(--grid-gap-half)',
    padding: 'var(--grid-gap-half)',
    width: 'auto',
    maxWidth: '100vw',

    [theme.breakpoints.up('md')]: {
      padding: 'var(--grid-gap-half)'
    }
  }),

  navItemSubMenuItem: ({ theme }) => ({
    'display': 'flex',
    'flexDirection': 'column',
    'width': 'auto',
    'padding': 0,
    'gap': 'var(--grid-gap-double)',

    // Style for column headers (AI Agents, Web Development)
    '& > [data-variant="group"]': {
      ...theme.typography.bodyLarge,
      margin: 0
    },

    // Style for sub-navigation items
    '& > div > div': {
      marginBottom: 'var(--grid-gap-half)'
    },

    [theme.breakpoints.up('md')]: {
      width: 'auto'
    }
  }),

  navItemGroup: ({ theme }) => ({
    'display': 'flex',
    'flexDirection': 'column',

    // Style for sub-navigation items
    '& > div': {
      marginBottom: 'var(--grid-gap-half)'
    },

    // Prevent additional hover effects
    '&:hover, &:focus-within': {
      // No additional hover effects
    },

    [theme.breakpoints.only('md')]: {
      paddingRight: 0,
      paddingLeft: 0
    }
  }),

  // Add styles for featured items
  featuredItem: ({ theme }) => ({
    display: 'flex',
    flexDirection: 'row', // Change to row to align image and text horizontally
    alignItems: 'flex-start',
    gap: 'var(--grid-gap-half)',
    width: '100%',
    padding: 'var(--grid-gap-half)',
    borderRadius: 'var(--grid-gap-half)',
    transition: 'background-color 0.2s ease'

    // '&:hover': {
    //   backgroundColor: 'rgba(0, 0, 0, 0.05)'
    // }
  }),

  featuredImage: ({ theme }) => ({
    'flexShrink': 0,
    'width': '48px',
    'height': '48px',

    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'contain'
    }
  }),

  featuredSummary: ({ theme }) => ({
    ...theme.typography.bodySmall,
    'maxWidth': '50vw', // Increase from 250px to 300px
    'whiteSpace': 'normal',
    'overflow': 'visible',
    'textOverflow': 'ellipsis',
    'display': '-webkit-box',
    'WebkitLineClamp': 2, // Limit to 2 lines
    'WebkitBoxOrient': 'vertical',

    '& p': {
      margin: 0,
      whiteSpace: 'normal',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  })
};

const createVariants = (_theme: Theme): ComponentsVariants['HeaderNavGroup'] => [];

export const headerNavGroupTheme = (theme: Theme): ThemeOptions => ({
  components: {
    HeaderNavGroup: {
      defaultProps,
      styleOverrides,
      variants: createVariants(theme)
    }
  }
});

export default headerNavGroupTheme;
